import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateCandidateInStore } from "../../Redux/candidatesSlice";
import { updateCandidateStatus } from "../../Action/api";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  Button,
  Divider,
  Grid,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  LocationOn,
  WorkHistory,
  OpenInNew,
  LinkedIn,
  Language,
  Phone,
  Email,
  Fingerprint,
  CalendarToday,
  Work,
  CurrencyRupee,
  Save,
  CheckCircle,
} from "@mui/icons-material";

const STATUS_OPTIONS = [
  { value: "Applied",             label: "Applied",              bg: "#f1f5f9", color: "#475569" },
  { value: "Under Review",        label: "Under Review",         bg: "#eff6ff", color: "#2563eb" },
  { value: "Shortlisted",         label: "Shortlisted",          bg: "#fdf4ff", color: "#9333ea" },
  { value: "Interview Scheduled", label: "Interview Scheduled",  bg: "#fef3c7", color: "#d97706" },
  { value: "Offer Extended",      label: "Offer Extended",       bg: "#ecfdf5", color: "#059669" },
  { value: "Hired",               label: "Hired",                bg: "#dcfce7", color: "#16a34a" },
  { value: "Rejected",            label: "Rejected",             bg: "#fef2f2", color: "#dc2626" },
  { value: "On Hold",             label: "On Hold",              bg: "#fff7ed", color: "#ea580c" },
];

const statusStyle = (val) =>
  STATUS_OPTIONS.find((s) => s.value === val) || { bg: "#f1f5f9", color: "#475569" };

const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
    <Box sx={{ mt: 0.15, flexShrink: 0, color: "#94a3b8" }}>{icon}</Box>
    <Box>
      <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.87rem", color: "#1e293b", fontWeight: 500, wordBreak: "break-word" }}>
        {value || <span style={{ color: "#cbd5e1" }}>—</span>}
      </Typography>
    </Box>
  </Stack>
);

const CandidateDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const candidate = location.state?.candidate;

  const [status, setStatus] = useState(candidate?.status || "Applied");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);

  if (!candidate) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Stack alignItems="center" spacing={2}>
          <Typography sx={{ color: "#94a3b8", fontWeight: 600 }}>Candidate data not found.</Typography>
          <Button variant="outlined" onClick={() => navigate("/dashboard")} startIcon={<ArrowBack />}
            sx={{ textTransform: "none" }}>
            Back to Dashboard
          </Button>
        </Stack>
      </Box>
    );
  }

  const initials = candidate.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const appliedDate = new Date(candidate.appliedAt).toLocaleDateString("en-IN", {
    day: "2-digit", month: "long", year: "numeric",
  });

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    const payload = {
      monthYear: candidate.monthYear,
      timeStamp: candidate.timeStamp,
      status,
    };
    const result = await updateCandidateStatus(payload);
    if (result.success) {
      // Update Redux cache so the dashboard table reflects the new status
      // without needing a re-fetch when navigating back
      dispatch(updateCandidateInStore({
        monthYear: candidate.monthYear,
        timeStamp: candidate.timeStamp,
        status,
      }));
      setSnackOpen(true);
    } else {
      setSaveError(result.message || "Failed to update status");
    }
    setSaving(false);
  };

  const currentStatus = statusStyle(status);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 3 }}>
      <Container maxWidth="lg">

        {/* ── Back bar ── */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <IconButton
            size="small"
            onClick={() => navigate("/dashboard")}
            sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f1f5f9" } }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, color: "#64748b", fontSize: "0.87rem" }}>
            Back to Dashboard
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            icon={<Fingerprint sx={{ fontSize: "14px !important", color: "#6366f1 !important" }} />}
            label={candidate.profileId}
            size="small"
            sx={{ fontFamily: "monospace", fontWeight: 800, bgcolor: "#f5f3ff", color: "#4f46e5", border: "1px solid #c7d2fe", letterSpacing: 1 }}
          />
        </Stack>

        <Grid container spacing={3}>

          {/* ── Left: Candidate Info ── */}
          <Grid item xs={12} md={8}>

            {/* Profile card */}
            <Paper sx={{ borderRadius: 3, border: "1px solid #f1f5f9", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", mb: 3, overflow: "hidden" }}>
              {/* Gradient header */}
              <Box sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", px: 3, py: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{ width: 56, height: 56, bgcolor: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 800, fontSize: "1.2rem" }}
                  >
                    {initials}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: "#fff", lineHeight: 1.2 }}>
                      {candidate.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.75)", mt: 0.3 }}>
                      Applied for <strong>{candidate.jobTitle}</strong>
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Quick chips */}
              <Box sx={{ px: 3, py: 1.5, bgcolor: "#fafbfc", borderBottom: "1px solid #f1f5f9" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={candidate.department} size="small" sx={{ bgcolor: "#eff6ff", color: "#2563eb", fontWeight: 700, fontSize: "0.73rem" }} />
                  <Chip label={candidate.jobType} size="small" sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 700, fontSize: "0.73rem" }} />
                  <Chip label={candidate.workMode} size="small" sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 700, fontSize: "0.73rem" }} />
                  {candidate.salary && (
                    <Chip
                      icon={<CurrencyRupee sx={{ fontSize: "12px !important" }} />}
                      label={candidate.salary}
                      size="small"
                      sx={{ bgcolor: "#f0fdfa", color: "#0d9488", fontWeight: 700, fontSize: "0.73rem" }}
                    />
                  )}
                </Stack>
              </Box>

              {/* Contact & job info grid */}
              <Box sx={{ px: 3, py: 2 }}>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={6}>
                    <InfoRow icon={<Email sx={{ fontSize: 16 }} />} label="Email" value={candidate.email} />
                    <InfoRow icon={<Phone sx={{ fontSize: 16 }} />} label="Phone" value={candidate.phone} />
                    <InfoRow icon={<LocationOn sx={{ fontSize: 16 }} />} label="Location" value={candidate.location} />
                    <InfoRow icon={<WorkHistory sx={{ fontSize: 16 }} />} label="Experience" value={candidate.experience || "Not specified"} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow icon={<Work sx={{ fontSize: 16 }} />} label="Department" value={candidate.department} />
                    <InfoRow icon={<CalendarToday sx={{ fontSize: 16 }} />} label="Applied On" value={appliedDate} />
                    {candidate.linkedin && (
                      <InfoRow icon={<LinkedIn sx={{ fontSize: 16 }} />} label="LinkedIn" value={candidate.linkedin} />
                    )}
                    {candidate.portfolio && (
                      <InfoRow icon={<Language sx={{ fontSize: 16 }} />} label="Portfolio" value={candidate.portfolio} />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Cover Letter */}
            {candidate.coverLetter && (
              <Paper sx={{ borderRadius: 3, border: "1px solid #f1f5f9", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", p: 3 }}>
                <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                  Cover Letter
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  sx={{ fontSize: "0.875rem", color: "#334155", lineHeight: 1.9, whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                >
                  {candidate.coverLetter}
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* ── Right: Actions ── */}
          <Grid item xs={12} md={4}>

            {/* Status update card */}
            <Paper sx={{ borderRadius: 3, border: "1px solid #f1f5f9", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", p: 3, mb: 3 }}>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.9rem", mb: 2 }}>
                Application Status
              </Typography>

              {/* Current status badge */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  bgcolor: currentStatus.bg,
                  border: `1.5px solid ${currentStatus.color}30`,
                  py: 1.2,
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 800, color: currentStatus.color, fontSize: "0.9rem" }}>
                  {status}
                </Typography>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel sx={{ fontSize: "0.85rem" }}>Change Status</InputLabel>
                <Select
                  value={status}
                  label="Change Status"
                  onChange={(e) => setStatus(e.target.value)}
                  sx={{ borderRadius: 2, fontSize: "0.85rem" }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "0.85rem" }}>{s.label}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {saveError && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 1.5, fontSize: "0.8rem" }}>{saveError}</Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                startIcon={saving ? null : <Save sx={{ fontSize: 17 }} />}
                disabled={saving}
                onClick={handleSave}
                sx={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                  py: 1.2,
                  boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
                  "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" },
                }}
              >
                {saving ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Save Status"}
              </Button>
            </Paper>

            {/* Resume */}
            <Paper sx={{ borderRadius: 3, border: "1px solid #f1f5f9", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", p: 3, mb: 3 }}>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.9rem", mb: 2 }}>
                Resume
              </Typography>
              {candidate.resumeUrl ? (
                <Button
                  fullWidth
                  variant="outlined"
                  component="a"
                  href={candidate.resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    borderColor: "#16a34a",
                    color: "#16a34a",
                    py: 1.2,
                    "&:hover": { bgcolor: "#f0fdf4", borderColor: "#15803d" },
                  }}
                >
                  View / Download Resume
                </Button>
              ) : (
                <Typography sx={{ fontSize: "0.82rem", color: "#94a3b8", textAlign: "center" }}>
                  No resume uploaded
                </Typography>
              )}
            </Paper>

            {/* Meta */}
            <Paper sx={{ borderRadius: 3, border: "1px solid #f1f5f9", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", p: 3 }}>
              <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.9rem", mb: 1.5 }}>
                Reference Info
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>Profile ID</Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontFamily: "monospace", fontWeight: 800, color: "#4f46e5" }}>
                    {candidate.profileId}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>Month</Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#1e293b" }}>{candidate.monthYear}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>Job ID</Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#1e293b" }}>#{candidate.jobId}</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* ── Success snackbar ── */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          icon={<CheckCircle fontSize="inherit" />}
          severity="success"
          sx={{ borderRadius: 2, fontWeight: 600 }}
          onClose={() => setSnackOpen(false)}
        >
          Status updated to <strong>{status}</strong>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CandidateDetail;
